import { useState } from 'react';
import { tokens } from '../tokens';
import useIsDesktop from '../useIsDesktop';

export default function LegalModal({ title, updated, onClose, sections }) {
  const isDesktop = useIsDesktop();
  const [step, setStep] = useState(0);
  const total = sections.length;
  const current = sections[step];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(6, 4, 3, 0.88)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        animation: `gl-fade 250ms ${tokens.ease}`,
        padding: isDesktop ? 40 : 0,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: isDesktop ? 640 : '100%',
          height: isDesktop ? 'auto' : '100%',
          maxHeight: isDesktop ? 'calc(100vh - 80px)' : '100%',
          background: tokens.ground,
          border: isDesktop ? `1px solid ${tokens.edge}` : 'none',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          animation: `gl-fade-up 350ms ${tokens.ease}`,
        }}
      >
        {/* Header — title + step counter + close */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: isDesktop ? '22px 32px 18px' : '18px 22px 14px',
          borderBottom: `1px solid ${tokens.edge}`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
            <h1 style={{
              fontFamily: tokens.fontDisplay,
              fontSize: isDesktop ? 22 : 20,
              fontWeight: tokens.displayWeight,
              letterSpacing: tokens.displayTracking,
              color: tokens.accent,
              margin: 0,
            }}>{title}</h1>
            <span style={{
              fontFamily: tokens.fontBody, fontSize: 11,
              color: tokens.textDim, letterSpacing: '0.06em',
            }}>{step + 1} / {total}</span>
          </div>
          <button
            className="gl-btn-bare"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${tokens.edge}`, borderRadius: '50%',
              color: tokens.textMid,
              fontFamily: tokens.fontBody, fontSize: 18, lineHeight: 1,
              flexShrink: 0,
            }}
          >&times;</button>
        </div>

        {/* Content — single section */}
        <div
          key={step}
          style={{
            flex: 1, overflowY: 'auto',
            padding: isDesktop ? '36px 32px 32px' : '28px 22px 28px',
            animation: `gl-fade-up 300ms ${tokens.ease}`,
          }}
        >
          {current.heading && (
            <h2 style={{
              fontFamily: tokens.fontDisplay,
              fontSize: isDesktop ? 26 : 22,
              fontWeight: tokens.displayWeight,
              letterSpacing: tokens.displayTracking,
              color: tokens.textHi,
              margin: '0 0 18px',
              lineHeight: 1.25,
            }}>{current.heading}</h2>
          )}
          <div>{current.content}</div>
          {updated && step === 0 && (
            <div style={{
              fontFamily: tokens.fontBody, fontSize: 11,
              color: tokens.textDim, marginTop: 20,
            }}>{updated}</div>
          )}
        </div>

        {/* Footer — progress + nav */}
        <div style={{
          padding: isDesktop ? '14px 32px 22px' : '12px 22px 28px',
          borderTop: `1px solid ${tokens.edge}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <button
            className="gl-btn-bare"
            onClick={() => step > 0 && setStep(step - 1)}
            disabled={step === 0}
            style={{
              fontFamily: tokens.fontBody, fontSize: 13,
              color: step === 0 ? tokens.textDim : tokens.textLow,
              letterSpacing: '0.04em',
              padding: '6px 4px',
              minWidth: 60,
            }}
          >{step > 0 ? '\u2190 Back' : ''}</button>

          {/* Progress dots */}
          <div style={{ display: 'flex', gap: 5 }}>
            {sections.map((_, i) => (
              <button
                key={i}
                className="gl-btn-bare"
                onClick={() => setStep(i)}
                aria-label={`Section ${i + 1}`}
                style={{
                  width: i === step ? 18 : 6,
                  height: 3,
                  background: i === step ? tokens.accent : i < step ? tokens.accentDim : tokens.textDim,
                  transition: `all 300ms ${tokens.ease}`,
                }}
              />
            ))}
          </div>

          <button
            className="gl-btn-bare"
            onClick={() => step < total - 1 ? setStep(step + 1) : onClose()}
            style={{
              fontFamily: tokens.fontBody, fontSize: 13,
              color: tokens.accent,
              letterSpacing: '0.04em',
              padding: '8px 14px',
              border: `1px solid ${tokens.accent}`,
              background: tokens.accentGlow,
              minWidth: 60,
              textAlign: 'center',
            }}
          >{step < total - 1 ? 'Next' : 'Close'}</button>
        </div>
      </div>
    </div>
  );
}

/* Shared styles for legal section content */
export const ls = {
  body: {
    fontFamily: tokens.fontBody,
    fontSize: 15,
    color: tokens.textMid,
    lineHeight: 1.7,
    margin: 0,
  },
  list: {
    fontFamily: tokens.fontBody,
    fontSize: 15,
    color: tokens.textMid,
    lineHeight: 1.7,
    paddingLeft: 20,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  sub: {
    fontFamily: tokens.fontDisplay,
    fontSize: 16,
    fontStyle: 'italic',
    color: tokens.textHi,
    marginTop: 18,
    marginBottom: 8,
  },
  strong: { color: tokens.textHi },
  note: {
    fontFamily: tokens.fontBody,
    fontSize: 12,
    color: tokens.textDim,
    marginTop: 16,
  },
};
